// Amadeus upstream calls — service_handler wrappers only. Every function
// returns either a Promise resolving to a normalized FlightOffer[] / catalog
// / raw service response. Raw envelopes never leak out of this module:
// responses are either normalized through `../normalize` or consumed
// directly by the caller for its own parsing (seatmaps, upsell).
//
// Each function takes the provider instance as its first argument so side
// effects (dictionary cache, extra-bags stash) land on the right instance.

import { service_handler } from '@/services';
import { deep_clone } from '@/utilities/';
import { log } from '@/utilities/commons';
import {
  rybbit_track_event,
  format_service_error,
} from '@/utilities/integrations/rybbit';
import { as_compare_string } from '@liveflight/utilities/mixins';
import { slim_automated_checkin_offer } from '@liveflight/utilities/rybbit';
import { apply_markup_to_number } from '@liveflight/utilities/price';
import { can_all_passengers_select_one_seat } from '@liveflight/utilities/seat-map';
import { normalize, extract_offers_array } from './normalize';
import { make_price, merge_dictionaries } from '../base/helpers';
import { extract_dictionaries } from './dictionaries';
import { build_mapped_decks, validate_traveler_pricing } from './seats';
import { build_book_body, build_search_request } from './request';

/**
 * Parse a pre-fetched Amadeus search response into normalized
 * FlightOffer[]. Used by `search` after service_handler resolves and
 * exposed for tests / offline fixtures.
 *
 * @param {Provider} provider
 * @param {Object} response
 * @returns {Object[]}
 */
export const parse_search_response = (provider, response) => {
  merge_dictionaries(provider, extract_dictionaries(response));
  return extract_offers_array(response).map((raw_offer) =>
    normalize(provider, raw_offer)
  );
};

/**
 * Generic search entry point. Amadeus is the default provider so the
 * service key is unprefixed.
 *
 * @param {Provider} provider
 * @param {Object} payload generic search request body
 * @returns {Promise<Object[]>}
 */
export const search = (provider, payload) =>
  service_handler('flightOffers', build_search_request(payload)).then(
    (response) => parse_search_response(provider, response)
  );

/**
 * Amadeus no-results suggestions. Fans out two variants of the original
 * search payload — wider origin/destination radius, and a ±3-day window
 * around each date — and returns their normalized offers in separate
 * buckets. The UI uses the radius bucket to suggest nearby airports and
 * the time-window bucket to suggest neighbouring dates.
 *
 * Suggestion search is gated on payload shape: Amadeus only supports the
 * widened-search variants for one-way / round-trip itineraries
 * (originDestinations.length 1 or 2). Multi-itinerary requests resolve
 * to empty buckets — there's no documented widening behaviour for them.
 *
 * Both variants are issued in parallel; a single variant failing must not
 * empty the other bucket — Promise.allSettled lets each settle on its own.
 *
 * @param {Provider} provider
 * @param {Object} payload generic search request body
 * @returns {Promise<{ by_radius: Object[], by_time_window: Object[] }>}
 */
export const fetch_search_suggestion = (provider, payload) => {
  const empty = { by_radius: [], by_time_window: [] };
  const { originDestinations = [] } = payload || {};
  if (originDestinations.length === 0 || originDestinations.length > 2) {
    return Promise.resolve(empty);
  }

  const RADIUS_WIDEN_KM = 100;
  const request_by_radius = deep_clone(payload);
  request_by_radius.originDestinations.forEach((o) => {
    o.originRadius = Math.max(Number(o.originRadius) || 0, RADIUS_WIDEN_KM);
    o.destinationRadius = Math.max(
      Number(o.destinationRadius) || 0,
      RADIUS_WIDEN_KM
    );
  });
  const { searchCriteria: radius_criteria = {} } = request_by_radius;
  if (radius_criteria.flightFilters) {
    radius_criteria.flightFilters.returnToDepartureAirport = false;
  }

  const request_by_time_window = deep_clone(payload);
  request_by_time_window.originDestinations.forEach((o) => {
    o.departureDateTimeRange = o.departureDateTimeRange || {};
    o.departureDateTimeRange.dateWindow = 'I3D';
  });

  return Promise.allSettled([
    search(provider, request_by_radius),
    search(provider, request_by_time_window),
  ]).then((settled) => ({
    by_radius:
      settled[0].status === 'fulfilled' && Array.isArray(settled[0].value)
        ? settled[0].value
        : [],
    by_time_window:
      settled[1].status === 'fulfilled' && Array.isArray(settled[1].value)
        ? settled[1].value
        : [],
  }));
};

/**
 * flightOffersPrice returns a repriced offer + envelope-level data
 * (bookingRequirements, dictionaries, included.bags). Caches the
 * extra-bags catalog on the provider so fetch_extra_bags can serve it
 * without a second round-trip.
 *
 * @param {Provider} provider
 * @param {Object} flight_offer
 * @returns {Promise<Object>}
 */
export const get_offer_details = (provider, flight_offer) => {
  const body = {
    priceFlightOffersBody: {
      data: {
        type: 'flight-offers-pricing',
        flightOffers: [flight_offer._raw],
      },
    },
  };
  if (provider.has_capability('shopBags')) {
    body.include = ['bags'];
  }
  return service_handler('flightOffersPrice', body).then((response) => {
    const { data: envelope = {} } = response || {};
    const { included = {}, data: priced_data = {} } = envelope;
    const { flightOffers = [] } = priced_data;
    merge_dictionaries(provider, extract_dictionaries(response));
    provider._extra_bags_catalog = included.bags || null;
    const priced = flightOffers[0];
    if (!priced) {
      throw new Error(
        '[AmadeusProvider] get_offer_details: reprice returned no flightOffers'
      );
    }
    return normalize(provider, priced);
  });
};

const NO_CHECKIN_OFFER = { available: false, price: null };

/**
 * The optional service fee on a `checkinOffer` envelope, in the canonical
 * Price shape. `display` is whatever the backend preformatted; empty when it
 * sent none, and the panel formats the amount itself.
 *
 * The amount is customer-facing — prefers `grandTotal`, and no markup is ever
 * applied on top of it. A missing or zero fee resolves to null: the service is
 * free, which is the only shape production sends today.
 *
 * @param {Object} [raw_price]
 * @returns {Object|null}
 */
const build_checkin_price = (raw_price) => {
  const {
    total = '',
    grandTotal = '',
    currency = 'EUR',
    display = '',
  } = raw_price || {};
  const amount = grandTotal === '' ? total : grandTotal;
  if (!Number(amount)) {
    return null;
  }
  return {
    ...make_price(String(amount), currency),
    display: String(display),
  };
};

/**
 * Automated check-in offer probe. Capability-gated: resolves to unavailable
 * without a network call when `automatedCheckIn` is off (the `checkinOffer`
 * endpoint is absent). Sends the frozen `_raw` offer — the cancellara endpoint
 * rebuilds passengers/journeys from the Amadeus order shape, not the
 * normalized one. Probe-only (`modal: false`); any failure (HTTP 511
 * integration-off / carrier-ineligible, network) resolves to "not available".
 *
 * Resolves to `{ available, price }` rather than a bare boolean so a priced
 * service can reach checkout. `price` is null whenever the backend quotes no
 * fee, which is every response today.
 *
 * Tracked by hand: `service_handler`'s `track` option fixes its context before
 * the call, so its success event could not carry the availability and price.
 *
 * @param {Provider} provider
 * @param {Object} flight_offer normalized offer (carries the frozen `_raw`)
 * @param {string} [citizenship] traveler country (ISO), defaults to 'IT'
 * @returns {Promise<{available: boolean, price: Object|null}>}
 */
export const automated_checkin_offer = (
  provider,
  flight_offer,
  citizenship = 'IT'
) => {
  if (!provider.has_capability('automatedCheckIn')) {
    return Promise.resolve(NO_CHECKIN_OFFER);
  }
  rybbit_track_event('automated-checkin:request', { type: 'info' });
  return service_handler(
    'checkinOffer',
    { flightOffer: flight_offer._raw, citizenship },
    { modal: false }
  )
    .then((response) => {
      const { data: envelope = {} } = response || {};
      const available = !!envelope.available;
      let price = null;
      if (available) {
        price = build_checkin_price(envelope.price);
      }
      rybbit_track_event(
        'automated-checkin:request:success',
        slim_automated_checkin_offer({ available, price })
      );
      if (!available) {
        return NO_CHECKIN_OFFER;
      }
      return { available: true, price };
    })
    .catch((error) => {
      rybbit_track_event('automated-checkin:request:fail', {
        type: 'error',
        ...format_service_error(error),
      });
      return NO_CHECKIN_OFFER;
    });
};

/**
 * Fetch the Amadeus seatmap and return a fully-normalized response.
 * Validation, per-deck mapping, and per-deck disabled-gating all happen
 * here so seat-selection.vue only reads pre-built data.
 *
 * @param {Provider} provider
 * @param {Object} flight_offer
 * @param {{ areInfantsPresent?: boolean, allowMalformed?: boolean }} [options={}]
 * @returns {Promise<Object|null>}
 */
export const fetch_seatmap = (provider, flight_offer, options = {}) => {
  if (!provider.has_capability('shopSeats')) {
    return Promise.resolve(null);
  }
  return service_handler('seatmaps', { flightOffer: flight_offer._raw }).then(
    (response) => {
      const { data: envelope = {} } = response || {};
      const { seatmaps = null } = envelope;
      if (!seatmaps) return null;
      const seatmapData = seatmaps.data || [];
      const isValid = validate_traveler_pricing(flight_offer, seatmapData);
      return {
        data: { seatmaps },
        dictionaries: seatmaps.dictionaries || {},
        empty: !seatmapData.length,
        isValid,
        canAllPassengersSelectOneSeat:
          can_all_passengers_select_one_seat(seatmapData),
        mappedDecks: isValid
          ? build_mapped_decks(flight_offer, seatmaps, options)
          : {},
      };
    }
  );
};

/**
 * Amadeus ships the extra-bags catalog in the same flightOffersPrice
 * response that get_offer_details already consumes (`include=['bags']`).
 * get_offer_details caches it on the instance so this resolves without
 * an upstream call. See providers/types.js::ExtraBag.
 *
 * @param {Provider} provider
 * @param {Object} flight_offer
 * @returns {Promise<Object|null>} canonical ExtraBagCatalog
 */
export const fetch_extra_bags = (provider, flight_offer) => {
  if (!provider.has_capability('shopBags')) {
    return Promise.resolve(null);
  }
  const raw_catalog = provider._extra_bags_catalog;
  if (!raw_catalog) return Promise.resolve(null);
  const { price: offer_price = {} } = flight_offer || {};
  const catalog = {};
  Object.keys(raw_catalog).forEach((bag_id) => {
    const raw = raw_catalog[bag_id];
    const { price: raw_price = {} } = raw;
    const raw_amount = raw_price.amount;
    const total_number = Number(raw_amount || 0);
    // Preserve trailing zeros — `String(Number("12.50"))` drops them.
    let total = String(total_number);
    if (typeof raw_amount === 'string' && raw_amount.length > 0) {
      total = raw_amount;
    }
    const grandTotal = String(
      apply_markup_to_number(flight_offer, total_number)
    );
    catalog[bag_id] = {
      id: raw.id,
      name: raw.name,
      quantity: raw.quantity,
      weight: raw.weight,
      weightUnit: raw.weightUnit,
      travelerIds: raw.travelerIds || [],
      segmentIds: raw.segmentIds || [],
      bookableByItinerary: !!raw.bookableByItinerary,
      price: {
        total,
        grandTotal,
        base: '',
        taxes: [],
        currency: offer_price.currency || raw_price.currency || '',
        currencySymbol: offer_price.currencySymbol || '',
      },
      _provider: provider.providerId,
      _raw: raw,
    };
  });
  return Promise.resolve(catalog);
};

/**
 * Translate the canonical CheckoutRequest into Amadeus's flight-order
 * wire body and POST it. The wire shape lives in ./request.js so its
 * concerns stay in one place. Amadeus /orders rejects anything that
 * isn't the original raw flight-offer shape, so build_book_body swaps
 * each entry in flightOffers to its _raw twin before hitting the wire.
 *
 * @param {Provider} _provider
 * @param {Object} flight_offer
 * @param {Object} request canonical CheckoutRequest
 * @returns {Promise<Object>}
 */
export const book = (_provider, flight_offer, request) => {
  const { automatedCheckInRequested = false } = request || {};
  return service_handler('book', build_book_body(flight_offer, request), {
    track: {
      name: 'checkout',
      context: {
        automated_checkin_requested: automatedCheckInRequested ? 1 : 0,
      },
    },
  });
};

/**
 * Surface the canonical confirmation shape from a /confirm response.
 * `voucher`. Offer entries are normalized canonical
 * FlightOffers; travelers pass through as-is.
 *
 * @param {Provider} provider
 * @param {Object} body parsed /confirm response body
 * @returns {Object}
 */
export const get_confirmation = (provider, body) => {
  const safe_body = body || {};
  const { flightVoucher: raw_voucher = {}, checkin, ...rest } = safe_body;
  const { orderData = {}, ...voucher_root } = raw_voucher;
  const { data = {} } = orderData;
  merge_dictionaries(provider, extract_dictionaries(safe_body));
  const raw_offers = Array.isArray(data.flightOffers) ? data.flightOffers : [];
  // Skip — never throw on — an offer that can't be normalized, so status,
  // voucher and tickets still reach the confirm page.
  const flight_offers = raw_offers.reduce((acc, raw) => {
    try {
      acc.push(normalize(provider, raw, { lenient_price: true }));
    } catch (e) {
      log('[amadeus/upstream] get_confirmation: offer normalize failed', e);
      rybbit_track_event('error:confirmation:offer_normalize', {
        ...format_service_error(e),
        flight_voucher_id: voucher_root.flightVoucherId,
        offer_id: raw && raw.id,
      });
    }
    return acc;
  }, []);
  
  const voucher = {
    ...voucher_root,
    flight_offers,
    travelers: Array.isArray(data.travelers) ? data.travelers : [],
    contacts: Array.isArray(data.contacts) ? data.contacts : [],
    tickets: Array.isArray(data.tickets) ? data.tickets : [],
  }

  if (
    provider.has_capability('automatedCheckIn') && 
    checkin && 
    as_compare_string(checkin.status_summary) !== 'not_requested'
  ) {
    voucher.automated_checkin_info = checkin
  }

  return {
    ...rest,
    voucher
  };
};
